import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RssFeedIcon from "@mui/icons-material/RssFeedOutlined";
import FiberNewIcon from "@mui/icons-material/FiberNewOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { TableSkeleton } from "../TableSkeleton";
import {
  foyerApi,
  type ContentPackageRow,
  type ContentSupplierRow,
  type FeedScanItem,
} from "../../http/foyer";
import { friendlyError } from "../../utils/apiError";
import { tokens } from "../../theme";
import {
  describeFeedAssetPlan,
  planFeedAsset,
  type FeedAssetValueSource,
} from "@shared/mrss-feed-asset";

/**
 * The review queue's Feed view (`[[mrss-feed-content-providers]]`).
 *
 * A provider with an MRSS feed, a Scan button, a diff against what has
 * already been ingested, and a review-then-approve drawer per new item -
 * see the plan for why approving mints the asset, registers the ingest job
 * and starts copying the master in ONE request (`feedIngest.ts`), with no
 * separate `content_submissions` hold: this drawer's own edit-then-Approve
 * IS the review, the same way a staff-authored `VodWizardDialog` title never
 * needs one either.
 */

export default function FeedReviewView() {
  const [suppliers, setSuppliers] = useState<ContentSupplierRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState<ContentSupplierRow | null>(null);

  useEffect(() => {
    let alive = true;
    foyerApi
      .listContentSuppliers()
      .then((res) => {
        if (!alive) return;
        // Only what this view can act on. A provider with no feed simply
        // never appears here - the same "no button where nothing is behind
        // it" stance the rest of this codebase takes.
        setSuppliers(res.suppliers.filter((s) => s.mrssFeedUrl));
      })
      .catch((e) => alive && setError(friendlyError(e)));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Card sx={{ overflow: "hidden" }}>
        {!suppliers ? (
          <TableSkeleton rows={3} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell>Feed</TableCell>
                  <TableCell sx={{ width: 120 }} align="right">
                    &nbsp;
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suppliers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Box sx={{ py: 5, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          No provider has an MRSS feed configured yet.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {suppliers.map((s) => (
                  <TableRow key={s.id} data-testid={`feed-supplier-${s.id}`}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {s.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ display: "block", maxWidth: 420 }}
                      >
                        {s.mrssFeedUrl}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        data-testid={`feed-scan-${s.id}`}
                        onClick={() => setScanning(s)}
                      >
                        Scan
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {scanning && (
        <FeedScanDialog supplier={scanning} onClose={() => setScanning(null)} />
      )}
    </>
  );
}

/**
 * The diff: every item the feed carries, marked against what this flow has
 * already turned into a real title.
 */
function FeedScanDialog({
  supplier,
  onClose,
}: {
  supplier: ContentSupplierRow;
  onClose: () => void;
}) {
  const [items, setItems] = useState<FeedScanItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [packages, setPackages] = useState<ContentPackageRow[]>([]);
  const [channelName, setChannelName] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<FeedScanItem | null>(null);
  // 🚨 Reported by Sigge: a full feed of a couple hundred items "feels long
  // for 83" new ones - the already-in rows are not actionable (no onClick,
  // nothing to review) and the stat cards already answer "how many", so by
  // default the table shows only the delta. Never hidden outright though -
  // an operator confirming a specific title already made it in still needs
  // to be able to look.
  //
  // Two earlier cuts of the control that flips this: a single button whose
  // own label changed state (not discoverable), then a separate New/All
  // toggle row (a second "New"/"All" next to the stat cards already saying
  // the same words). Settled on making the stat cards THE control - one
  // set of numbers, doubling as the filter, selection shown on the card
  // itself rather than in a second place naming the same things.
  const [view, setView] = useState<"new" | "all" | "in">("new");

  const load = useCallback(() => {
    setItems(null);
    setError(null);
    foyerApi
      .scanSupplierFeed(supplier.id)
      .then((res) => setItems(res.items))
      .catch((e) => setError(friendlyError(e)));
  }, [supplier.id]);

  useEffect(() => {
    load();
    foyerApi
      .listContentPackages()
      .then((res) => setPackages(res.packages))
      .catch(() => setPackages([]));
  }, [load]);

  // Named once, just for the reviewer's confidence that Approve does what
  // it says - the approval itself never needs the name, only the id already
  // on `supplier.effectChannelId`.
  useEffect(() => {
    if (!supplier.effectChannelId) return;
    let alive = true;
    foyerApi
      .listSignagePlaylistsIn({ kind: "global" })
      .then((rows) => {
        if (!alive) return;
        setChannelName(
          rows.find((p) => p.id === supplier.effectChannelId)?.name ?? null
        );
      })
      .catch(() => alive && setChannelName(null));
    return () => {
      alive = false;
    };
  }, [supplier.effectChannelId]);

  // Out first - that is the actual work this dialog exists for, and a
  // provider's feed can carry hundreds of already-ingested items ahead of
  // the handful worth a reviewer's time.
  const sorted = items
    ? [...items].sort((a, b) => Number(a.in) - Number(b.in))
    : null;
  const newCount = items ? items.filter((i) => !i.in).length : 0;
  const inCount = items ? items.length - newCount : 0;
  // Already-in rows are not actionable - no onClick, nothing to review - and
  // the stat cards above already answer "how many", so they are hidden by
  // default rather than padding the table with rows that feel long for a
  // handful of new titles.
  const visible = !sorted
    ? null
    : view === "new"
      ? sorted.filter((i) => !i.in)
      : view === "in"
        ? sorted.filter((i) => i.in)
        : sorted;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          fontSize: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>{supplier.name}&rsquo;s feed</span>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {/* The count itself is the point of scanning at all - an operator
            deciding whether a rescan is worth their time should not have to
            count rows to find out, so it leads the dialog rather than
            captioning the table under it. */}
        {items && (
          <Stack
            direction="row"
            gap={1.5}
            sx={{ mb: 3 }}
            data-testid="feed-scan-stats"
          >
            <StatCard
              icon={<RssFeedIcon sx={{ fontSize: 30 }} color="action" />}
              value={items.length}
              label={`title${items.length === 1 ? "" : "s"} in the feed`}
              testId="feed-scan-stat-total"
              selected={view === "all"}
              onClick={() => setView("all")}
            />
            <StatCard
              icon={<FiberNewIcon sx={{ fontSize: 30 }} color="warning" />}
              value={newCount}
              label="new"
              testId="feed-scan-stat-new"
              selected={view === "new"}
              onClick={() => setView("new")}
            />
            <StatCard
              icon={<CheckCircleIcon sx={{ fontSize: 30 }} color="success" />}
              value={inCount}
              label="already in the library"
              testId="feed-scan-stat-in"
              selected={view === "in"}
              onClick={() => setView("in")}
            />
          </Stack>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {channelName && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            data-testid="feed-target-channel"
          >
            New items join <strong>{channelName}</strong> automatically on
            approval.
          </Alert>
        )}
        {!sorted ? (
          <TableSkeleton rows={4} />
        ) : sorted.length === 0 ? (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              This feed has no items right now.
            </Typography>
          </Box>
        ) : (
          <>
            {visible && visible.length === 0 ? (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {view === "in"
                    ? "Nothing approved from this feed yet."
                    : "Nothing new since the last scan — every title in this feed is already in the library."}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 64 }} />
                      <TableCell>Title</TableCell>
                      <TableCell sx={{ width: 110 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(visible ?? []).map((item) => (
                      <TableRow
                        key={item.guid}
                        hover={!item.in}
                        data-testid={`feed-item-${item.guid}`}
                        sx={item.in ? undefined : { cursor: "pointer" }}
                        onClick={item.in ? undefined : () => setReviewing(item)}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              width: 48,
                              height: 32,
                              borderRadius: "6px",
                              overflow: "hidden",
                              bgcolor: tokens.rowAlt,
                            }}
                          >
                            {item.thumbnailUrl && (
                              <Box
                                component="img"
                                src={item.thumbnailUrl}
                                alt=""
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {item.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={item.in ? "success" : "default"}
                            label={item.in ? "In library" : "New"}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>

      {reviewing && (
        <ReviewFeedItemDrawer
          supplierId={supplier.id}
          channelName={channelName}
          item={reviewing}
          packages={packages}
          onClose={() => setReviewing(null)}
          onApproved={() => {
            setReviewing(null);
            // Moves the row to "in library" without a full rescan - a
            // provider's feed can be a few hundred items, and the operator
            // just watched this one get approved.
            setItems((prev) =>
              prev
                ? prev.map((i) =>
                    i.guid === reviewing.guid ? { ...i, in: true } : i
                  )
                : prev
            );
          }}
        />
      )}
    </Dialog>
  );
}

/**
 * Pictures, metadata, files, then Approve.
 *
 * Prefilled from the feed, editable: an operator reviewing DroneTV's own
 * wording is not obligated to ship it verbatim. The video/thumbnail preview
 * plays the feed's OWN hosted URL directly, deliberately not the SAS-and-
 * ingest-job player `RawPreview` uses elsewhere on this page - there is no
 * ingest job yet at review time, and AirVuz's links are plain, unsigned,
 * public files (confirmed against a real feed).
 *
 * ## The media-collections picker only exists for a supplier with NO channel
 *
 * `channelName` set means this supplier's items already have a destination -
 * its effect channel - and it is the ONLY correct one: packages are the
 * guest-facing reachability mechanism, and venue-ambience content joining
 * one would be exactly the "must never reach a guest room" violation
 * `effect-ffa`'s own doctrine warns about. The picker is not merely
 * de-emphasised here, it is absent - there is nothing correct it could do
 * for this supplier. It reappears only for a supplier with no channel
 * mapped, where packages remain the real (and only) reachability answer.
 */
function ReviewFeedItemDrawer({
  supplierId,
  channelName,
  item,
  packages,
  onClose,
  onApproved,
}: {
  supplierId: string;
  /** The supplier's mapped effect channel name, or `null` for a supplier with
   *  no channel mapped - see the component doc comment. */
  channelName: string | null;
  item: FeedScanItem;
  packages: ContentPackageRow[];
  onClose: () => void;
  onApproved: () => void;
}) {
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description);
  const [chosenPackages, setChosenPackages] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The exact payload the approve route will build from this item and these
  // edits - one shared function on both sides, so the table below cannot
  // promise a field the server does not write, or hide one it does.
  const edits = { title, synopsisLong: description };
  const writes = describeFeedAssetPlan(
    planFeedAsset(supplierId, item, edits),
    item,
    edits
  );

  async function approve() {
    setSaving(true);
    setError(null);
    try {
      await foyerApi.approveFeedItem(supplierId, item.guid, {
        title: title.trim(),
        synopsisLong: description.trim(),
        // Never sent for a channel-mapped supplier - there is no picker to
        // have set it, and sending the empty array either way is exactly
        // what "no collection chosen" already means server-side.
        packages: channelName ? [] : chosenPackages,
      });
      onApproved();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 16 }}>Review this clip</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 0.5 }}>
          <Box
            sx={{
              bgcolor: "#000",
              borderRadius: "10px",
              overflow: "hidden",
              display: "grid",
              placeItems: "center",
            }}
          >
            <video
              src={item.mediaUrl}
              poster={item.thumbnailUrl ?? undefined}
              controls
              playsInline
              data-testid="feed-item-preview"
              style={{ width: "100%", maxHeight: "40vh", display: "block" }}
            />
          </Box>

          <TextField
            size="small"
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            autoFocus
            inputProps={{ "data-testid": "feed-review-title" }}
          />
          <TextField
            size="small"
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            inputProps={{ "data-testid": "feed-review-description" }}
          />
          {channelName ? (
            <Alert severity="info" data-testid="feed-review-channel-note">
              Joins <strong>{channelName}</strong> automatically on approval —
              no media collection needed.
            </Alert>
          ) : (
            <TextField
              select
              size="small"
              label="Media collections"
              value={chosenPackages}
              onChange={(e) =>
                setChosenPackages(
                  typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : (e.target.value as unknown as string[])
                )
              }
              SelectProps={{
                multiple: true,
                renderValue: (selected) =>
                  (selected as string[])
                    .map((id) => packages.find((p) => p.id === id)?.name ?? id)
                    .join(", "),
              }}
              helperText="A title in no collection plays for nobody."
              inputProps={{ "data-testid": "feed-review-packages" }}
            >
              {packages.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <FeedFieldTable
            title="Written to the title on approval"
            testId="feed-review-writes"
            rows={writes}
          />

          {item.unmapped.length > 0 && (
            <FeedFieldTable
              title="In the feed, not written"
              caption="No catalogue field takes these yet, so they stay in the feed."
              testId="feed-review-unmapped"
              rows={item.unmapped.map((f, i) => ({
                key: `${f.field}-${i}`,
                label: f.field,
                value: f.value,
              }))}
            />
          )}

          {error && (
            <Typography sx={{ fontSize: 13, color: tokens.error }}>
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!title.trim() || saving}
          onClick={() => void approve()}
          data-testid="feed-review-approve"
        >
          {saving ? "Approving…" : "Approve"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const SOURCE_LABELS: Record<FeedAssetValueSource, string> = {
  feed: "From feed",
  edited: "Edited",
  derived: "Derived",
  default: "Default",
};

/**
 * A plain field/value list for the review dialog. `source` is optional: the
 * "written" table says where each value came from, the "not written" one has
 * nothing to say beyond the feed's own field name.
 */
function FeedFieldTable({
  title,
  caption,
  testId,
  rows,
}: {
  title: string;
  caption?: string;
  testId: string;
  rows: {
    key: string;
    label: string;
    value: string;
    source?: FeedAssetValueSource;
  }[];
}) {
  return (
    <Box data-testid={testId}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink }}>
        {title}
      </Typography>
      {caption && (
        <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
          {caption}
        </Typography>
      )}
      <TableContainer sx={{ mt: 0.5 }}>
        <Table size="small">
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.key} data-testid={`${testId}-${r.key}`}>
                <TableCell
                  sx={{
                    width: 150,
                    verticalAlign: "top",
                    color: tokens.inkMuted,
                    fontSize: 12,
                  }}
                >
                  {r.label}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: 13,
                    verticalAlign: "top",
                    overflowWrap: "anywhere",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {r.value}
                </TableCell>
                {r.source && (
                  <TableCell sx={{ width: 96, verticalAlign: "top" }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={r.source === "edited" ? "primary" : "default"}
                      label={SOURCE_LABELS[r.source]}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/** One big number with an icon - the scan's headline, not a caption. */
/**
 * Doubles as the filter control for the table below it (`view` in
 * `FeedScanDialog`) - not a separate New/All row saying the same three
 * words again. `selected` is the one visual difference from a plain stat:
 * a brand-tinted fill and border, the same "this is the active choice"
 * language already used for a selected row elsewhere (`tokens.brandTintSoft`).
 */
function StatCard({
  icon,
  value,
  label,
  testId,
  selected,
  onClick,
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
  testId: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <ButtonBase
      data-testid={testId}
      onClick={onClick}
      aria-pressed={selected}
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 1.5,
        p: 2,
        borderRadius: "12px",
        border: `1px solid ${selected ? tokens.brand : tokens.divider}`,
        bgcolor: selected ? tokens.brandTintSoft : tokens.rowAlt,
        textAlign: "left",
        transition: "border-color 0.15s, background-color 0.15s",
        "&:hover": { borderColor: tokens.brand },
      }}
    >
      {icon}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            color: tokens.ink,
          }}
        >
          {value}
        </Typography>
        <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
          {label}
        </Typography>
      </Box>
    </ButtonBase>
  );
}
